import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Beaker,
  FlaskConical,
  Atom,
  Droplets,
  CircleDot,
  Waves,
  Hexagon,
  Leaf,
  Activity,
  ChevronRight,
} from "lucide-react";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";

const applications = [
  {
    icon: Atom,
    title: "Protein Powders",
    desc: "Mask bitterness from plant and whey proteins; reduce chalk and metallic off notes.",
  },
  {
    icon: Droplets,
    title: "Hydration & Electrolytes",
    desc: "Smooth salinity, soften mineral edges, elevate perceived sweetness without more sugar.",
  },
  {
    icon: FlaskConical,
    title: "Functional Beverages",
    desc: "Balance actives, botanicals and adaptogens into clean, drinkable finished beverages.",
  },
  {
    icon: Leaf,
    title: "Nootropics & Adaptogens",
    desc: "Tame bitterness and astringency from mushrooms, nootropics, and herbal extracts.",
  },
];

const capabilities = [
  {
    n: "01",
    title: "Bitterness Reduction",
    desc: "Targeted blockers and modulators that suppress bitter receptor activation in high active formulations.",
  },
  {
    n: "02",
    title: "Astringency Control",
    desc: "Smooth tannin and protein driven dryness without masking clean flavor delivery.",
  },
  {
    n: "03",
    title: "Sweetness Enhancement",
    desc: "Lift perceived sweetness in reduced sugar and zero sugar systems while preserving flavor integrity.",
  },
  {
    n: "04",
    title: "Mouthfeel Improvement",
    desc: "Body and roundness for thin, watery matrices without added sugars or synthetic thickeners.",
  },
  {
    n: "05",
    title: "Flavor System Optimization",
    desc: "Integrated flavor + modulation systems engineered for the actives, pH, and stability of your matrix.",
  },
];

const whyPoints = [
  {
    icon: Hexagon,
    title: "Ingredient first approach",
    desc: "We deliver functional ingredient systems. Not marketing claims. Every tool we ship performs at the bench.",
  },
  {
    icon: Activity,
    title: "Built for real formulation challenges",
    desc: "Protein, caffeine, creatine, electrolytes, botanicals. We formulate into the hard stuff, not around it.",
  },
  {
    icon: Waves,
    title: "Fast development & sampling",
    desc: "Short iteration cycles. Samples in days, not quarters. Production ready specs when you scale.",
  },
  {
    icon: CircleDot,
    title: "Made for modern functional products",
    desc: "Clean labels, regulatory awareness, and sensory rigor aligned with today's health and performance categories.",
  },
];

export default function Home() {
  return (
    <div className="bg-white">
      {/* ---------- HERO ---------- */}
      <section
        data-testid="hero-section"
        className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
      >
        <div className="absolute inset-0 pm-grid-bg opacity-60 pointer-events-none" />
        <div className="absolute -top-20 right-[-120px] w-[620px] h-[620px] rounded-full bg-brand/5 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-8 animate-fade-in">
                <span className="pm-divider" />
                <span className="pm-overline">Flavor Modulation · B2B Ingredients</span>
              </div>
              <h1
                data-testid="hero-headline"
                aria-label="Flavor Modulation Systems for High Performance Formulations."
                className="font-heading font-semibold tracking-tight text-gray-900 text-[40px] leading-[1.05] sm:text-5xl lg:text-[64px] lg:leading-[1.02] animate-fade-up"
              >
                Flavor Modulation<br />
                Systems for{" "}
                <span className="text-brand">High&nbsp;Performance</span><br />
                Formulations.
              </h1>
              <p
                data-testid="hero-subtext"
                className="mt-8 max-w-xl text-base md:text-lg text-gray-600 leading-relaxed animate-fade-up"
                style={{ animationDelay: "120ms" }}
              >
                Helping brands improve taste, reduce bitterness, and enhance
                drinkability in functional nutrition products, from protein
                powders and hydration sticks to next generation functional
                beverages.
              </p>
              <div
                className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
                style={{ animationDelay: "220ms" }}
              >
                <Button
                  asChild
                  data-testid="hero-cta-sample"
                  className="bg-brand hover:bg-brand-hover text-white rounded-sm h-12 px-7 text-[15px] font-medium"
                >
                  <Link to="/contact">
                    Request a Sample
                    <ArrowUpRight className="ml-2" size={16} />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-cta-contact"
                  className="border-gray-300 hover:border-brand hover:text-brand rounded-sm h-12 px-7 text-[15px] font-medium bg-white"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>

              <div
                className="mt-16 grid grid-cols-3 max-w-xl border-t border-gray-100 pt-8 animate-fade-up"
                style={{ animationDelay: "320ms" }}
              >
                {[
                  { k: "Bitterness", v: "−45%", s: "avg. suppression index" },
                  { k: "Sweetness", v: "+30%", s: "perceived lift" },
                  { k: "Drinkability", v: "9/10", s: "sensory panels" },
                ].map((m) => (
                  <div key={m.k} className="pr-4">
                    <div className="pm-num text-3xl text-gray-900">{m.v}</div>
                    <div className="text-xs text-gray-500 mt-1">{m.k}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">
                      {m.s}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual panel */}
            <div className="lg:col-span-5 relative">
              <div
                className="relative aspect-[4/5] w-full rounded-sm overflow-hidden border border-gray-200 bg-gradient-to-b from-[#F9FAFB] to-white pm-molecule-fade animate-fade-in"
                style={{ animationDelay: "200ms" }}
                data-testid="hero-visual"
              >
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/d836de25-6805-483e-b4de-64a38c2f97c1/images/445a3061f8412d4e56ce79ea6bf1bfac2a94ab10ca23ee66104c07011d4a6a3a.png"
                  alt="Abstract molecular structure"
                  className="w-full h-full object-cover mix-blend-multiply"
                />
                <div className="absolute top-5 left-5 flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-1.5 border border-gray-200 rounded-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-gray-700 font-medium">
                    Molecular Modulation
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur border border-gray-200 rounded-sm p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">
                    Sample System · PM 08
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    Bitterness suppression for plant protein matrix
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- WHAT WE DO ---------- */}
      <Section testId="what-we-do-section" bg="muted">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="pm-overline">What We Do</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Ingredient based solutions for the taste problems formulators
              actually have.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Beaker,
                title: "Flavor Modulation Systems",
                body: "Proprietary blends that adjust perception of bitterness, sweetness, astringency, and mouthfeel across complex matrices.",
              },
              {
                icon: FlaskConical,
                title: "Ingredient Solutions",
                body: "Single ingredient modulators and custom built systems engineered to your actives, loadings, and process constraints.",
              },
              {
                icon: Droplets,
                title: "Powders & Beverages",
                body: "Focused on powdered supplements and functional beverages, where masking and modulation are hardest and matter most.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  data-testid={`what-we-do-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="pm-card p-6 md:p-7"
                >
                  <div className="w-10 h-10 rounded-sm bg-brand-subtle flex items-center justify-center text-brand mb-5">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ---------- APPLICATIONS ---------- */}
      <Section testId="applications-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="pm-overline">Applications</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Purpose built for the categories shaping modern nutrition.
            </h2>
          </div>
          <Link
            to="/applications"
            data-testid="applications-explore-link"
            className="text-sm font-medium text-brand pm-link inline-flex items-center gap-2"
          >
            Explore all applications <ChevronRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {applications.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                data-testid={`application-card-${i}`}
                className="pm-card p-7 h-full"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 rounded-sm bg-brand-subtle flex items-center justify-center text-brand">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-gray-400">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-base font-semibold text-gray-900 mb-2">
                  {a.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {a.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ---------- CAPABILITIES ---------- */}
      <Section testId="capabilities-section" bg="muted">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <span className="pm-overline">Capabilities</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
              Tools engineered for the sensory challenges of functional
              formulation.
            </h2>
            <p className="mt-6 text-gray-600 leading-relaxed text-sm">
              We focus on the narrow, technical problems that decide whether a
              product ships, and whether consumers come back.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ul className="divide-y divide-gray-200 border-t border-b border-gray-200">
              {capabilities.map((c) => (
                <li
                  key={c.n}
                  data-testid={`capability-row-${c.n}`}
                  className="group grid grid-cols-12 gap-4 py-6 md:py-7 items-start hover:bg-white transition-colors px-2 md:px-4 -mx-2 md:-mx-4"
                >
                  <div className="col-span-2 md:col-span-1 pm-num text-sm text-brand pt-1">
                    {c.n}
                  </div>
                  <div className="col-span-10 md:col-span-5 font-heading text-lg font-medium text-gray-900">
                    {c.title}
                  </div>
                  <div className="col-span-12 md:col-span-6 text-sm text-gray-600 leading-relaxed">
                    {c.desc}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ---------- WHY PREMIER ---------- */}
      <Section testId="why-section">
        <div className="max-w-3xl">
          <span className="pm-overline">Why Premier Modulations</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
            A technical partner. Not a marketing brand.
          </h2>
          <p className="mt-5 text-gray-600 leading-relaxed">
            We exist to help formulators solve the flavor problems that stand
            between a promising concept and a shelf ready product.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
          {whyPoints.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`why-point-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="pm-card p-7 md:p-8 flex gap-5"
              >
                <div className="shrink-0 w-11 h-11 rounded-sm bg-brand-subtle flex items-center justify-center text-brand">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ---------- FINAL CTA ---------- */}
      <section
        data-testid="final-cta-section"
        className="relative bg-white overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/d836de25-6805-483e-b4de-64a38c2f97c1/images/1442fbe8bdc46b2c825ae0ce3950c6c9207b8e156dffb30ae614c594a2830fc1.png"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-left">
          <span className="pm-overline">Start a Project</span>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight text-gray-900 leading-tight max-w-3xl">
            Better flavor.<br />Better formulations.
          </h2>
          <p className="mt-6 text-gray-700 max-w-2xl leading-relaxed">
            Send us your challenge: a difficult active, a taste complaint, a
            reformulation target. We'll review your system and propose a
            modulation approach.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              asChild
              data-testid="final-cta-sample"
              className="bg-brand hover:bg-brand-hover text-white rounded-sm h-12 px-7 text-[15px] font-medium"
            >
              <Link to="/contact">
                Request a Sample
                <ArrowUpRight className="ml-2" size={16} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="final-cta-contact"
              className="border-gray-300 hover:border-brand hover:text-brand rounded-sm h-12 px-7 text-[15px] font-medium bg-white"
            >
              <Link to="/contact">Contact Our Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
